
/** @format */

import React, { useEffect } from 'react';
import { Grid, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { InputV2, SelectV, AutocompleteV, InputCurrency } from 'components';
import { useStyles } from './Style';
import CreateSubscriptionServcies from '../../services/CreateSubscriptionServices';
import {
  SUBSCRIPTION_TYPES,
  SETTLEMENT_PERIOD_TYPE,
  DEFAULT_SUBSCRIPTION_TYPE,
  PERIOD_TYPES,
} from '../../../constants/SubscriptionConstants';
import useCustomHook from '../../../hooks';
import { CheckBoxIcon, CheckBoxIconFill } from 'components/CheckBox/CheckBoxIcon';

const BaseInfo = ({ id, noLimitSelected = false }) => {
  // -- HOOKS --//
  const methods = useFormContext();
  const classes = useStyles();
  const { guildsList, setGuildsList, guildListLoading, setGuildListLoading } = useCustomHook();

  useEffect(() => {
    getGuildsList();
  }, []);

  useEffect(() => {
    methods.register('type');
    methods.register('oneTimeUse');
    return () => {
      methods.unregister('type');
      methods.unregister('oneTimeUse');
    };
  }, [methods.register, methods.unregister]);

  // -- API REQUESTS --//
  const getGuildsList = async (val = '') => {
    setGuildListLoading(true);
    try {
      let q = {};
      if (val) {
        q = { query: encodeURI(val) };
      }

      const result = await CreateSubscriptionServcies.getCreditAccounts(q);
      const data = [...result.result.mainBusinessAmountSrvs];

      if (data.length > 0) {
        let list = [];
        list = data.map((val) => ({
          name:
            (val?.aliasName ?? val?.guildSrv?.name) +
            (val?.guildSrv?.code ? ` - ${val?.guildSrv?.code}` : ''),
          id: val.id,
          label:
            (val?.aliasName ?? val?.guildSrv?.name) +
            (val?.guildSrv?.code ? ` - ${val?.guildSrv?.code}` : ''),
          // + val?.guildSrv?.code
          value: val.id,
          code: val?.guildSrv?.code,
        }));

        setGuildsList(list);
      }
    } catch (e) {
      console.log('e :', e);
    }

    setGuildListLoading(false);
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      spacing={2}
      item
      className={classes.root}
      xs={12}
      lg={9}
    >
      <Grid item xs={12} md={4}>
        <InputV2
          fullWidth
          label="نام طرح"
          errors={methods.formState.errors}
          name="name"
          register={methods.register}
          rules={['required']}
          control={methods.control}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SelectV
          name="type"
          options={SUBSCRIPTION_TYPES}
          label="نوع طرح"
          control={methods.control}
          register={methods.register}
          rules={['required']}
          required
          tooltip="برای آشنایی با انواع طرح اشتراک به لینک راهنمای بالا صفحه مراجعه کنید."
          defaultValue={DEFAULT_SUBSCRIPTION_TYPE}
          errors={methods.formState.errors}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={4} />
      {methods.watch('type') !== 'SUBSCRIPTION_PLAN_TYPE_POST_PAID' ? (
        <Grid item xs={12} md={4}>
          <InputCurrency
            fullWidth
            label={
              methods.watch('type') === 'SUBSCRIPTION_PLAN_TYPE_BLOCK'
                ? 'حداکثر مبلغ مسدودی'
                : 'قیمت'
            }
            errors={methods.formState.errors}
            name="price"
            register={methods.register}
            rules={['required', 'isNumeric', 'isMin|0']}
            control={methods.control}
            minimumValue={0}
            decimalPlaces={0}
            currency
            englishNumber
          />
        </Grid>
      ) : null}
      {methods.watch('type') === 'SUBSCRIPTION_PLAN_TYPE_POST_PAID' ? (
        <>
          <Grid item xs={12} md={4}>
            <InputV2
              fullWidth
              label="سقف بدهکاری طرح"
              errors={methods.formState.errors}
              name="maxDebtorAmount"
              register={methods.register}
              rules={['required', 'isNumeric', 'isMin|0']}
              control={methods.control}
              minimumValue={0}
              decimalPlaces={0}
              currency
              englishNumber
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectV
              name="settlementPeriodTypeCode"
              options={SETTLEMENT_PERIOD_TYPE}
              label="دوره تسویه طرح"
              control={methods.control}
              register={methods.register}
              rules={['required']}
              required
              errors={methods.formState.errors}
              fullWidth
            />
          </Grid>
        </>
      ) : null}
      <Grid item xs={12}>
        <Typography className={classes.partTitle}>انتخاب بازه زمانی</Typography>
      </Grid>

      <Grid item xs={6} md={2}>
        <InputV2
          fullWidth
          errors={methods.formState.errors}
          rules={['required']}
          label="تعداد بازه"
          decimalPlaces={0}
          minimumValue={0}
          currencyText=""
          name="periodTypeCount"
          register={methods.register}
          control={methods.control}
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <SelectV
          name="periodTypeCode"
          options={PERIOD_TYPES}
          label="بازه زمانی"
          control={methods.control}
          register={methods.register}
          rules={['required']}
          required
          errors={methods.formState.errors}
          fullWidth
        />
      </Grid>
      {methods.watch('type') !== 'SUBSCRIPTION_PLAN_TYPE_BLOCK' ? (
        <>
          <Grid item xs={12}>
            <Typography className={classes.partTitle}>محدودیت استفاده</Typography>
            {}
          </Grid>
          <Grid item xs={6} md={4}>
            <InputV2
              fullWidth
              errors={methods.formState.errors}
              tooltip="با رسیدن دفعات استفاده به این تعداد واحد برسد، طرح اشتراک برای استفاده کننده پایان خواهد یافت."
              currency
              label="محدودیت دفعات قابل استفاده"
              decimalPlaces={0}
              minimumValue={0}
              currencyText="مرتبه"
              name="usageCountLimit"
              register={methods.register}
              control={methods.control}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <InputCurrency
              fullWidth
              errors={methods.formState.errors}
              tooltip="با رسیدن مجموع استفاده کاربر از محل این طرح اشتراک به این مبلغ، طرح اشتراک برای استفاده‌کننده پایان خواهد یافت."
              currency
              label="محدودیت مبلغ قابل استفاده"
              decimalPlaces={0}
              minimumValue={0}
              name="usageAmountLimit"
              register={methods.register}
              control={methods.control}
            />
          </Grid>
        </>
      ) : null}
      <Grid item xs={12}>
        <Typography className={classes.partTitle}>تنظیمات درآمد</Typography>
      </Grid>
      {!id ? (
        <>
          <Grid item xs={12} md={4}>
            <AutocompleteV
              placeholder="جستجو و انتخاب کنید..."
              control={methods.control}
              errors={methods.formState.errors}
              register={methods.register}
              name="guildCode"
              label="محل واریز درآمد"
              list={guildsList}
              preventAddCustomOption
              rules={['required']}
              loading={guildListLoading}
              onInputChange={(e) => {}}
              onChangeValue={(p) => {}}
            />
          </Grid>

          {}
        </>
      ) : null}
      <Grid item xs={6} md={4}>
        <InputV2
          fullWidth
          errors={methods.formState.errors}
          rules={['required']}
          currency
          label="درصد مالیات"
          minimumValue={0}
          currencyText="درصد"
          name="taxRate"
          register={methods.register}
          control={methods.control}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="oneTimeUse"
          control={methods.control}
          render={({ field: { onChange, value, ...rest } }) => {
            return (
              <FormControlLabel
                value={value}
                className={classes.oneTimeCheck}
                control={
                  <Checkbox
                    onChange={(e) => onChange(e.target.checked)}
                    checked={value ?? false}
                    icon={<CheckBoxIcon />}
                    checkedIcon={<CheckBoxIconFill />}
                  />
                }
                label="طرح یک‌بار‌مصرف می‌باشد"
              />
            );
          }}
        />
      </Grid>
    </Grid>
  );
};

export default BaseInfo;
